import { Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShellNotice } from '@/shell/shell-notice';

/** Set when the person closes the notice; never reset, so it stays closed in this browser. */
export const CUTOVER_NOTICE_DISMISSED_KEY = 'dgb-console:expenses-cutover-notice-dismissed';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(CUTOVER_NOTICE_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/** The expenses list and the notes list: the only pages the cutover notice shows on (#956). */
export function isExpensesListPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '');
  return path === '/expenses' || path === '/expenses/reports';
}

/**
 * The move from nexpense (#917, decisions 215 and 221): from 1 November new expenses and notes go
 * here, those up to October can still be worked on in nexpense, and nexpense is read-only from
 * 1 December. Worded like nexpense's own notice. No end date is decided, so it shows until it is
 * closed, on the two lists only (#956; it used to repeat on every module page).
 */
export function useCutoverNotice(onList: boolean): ShellNotice | null {
  const { t } = useTranslation('expenses');
  const [dismissed, setDismissed] = useState(isDismissed);
  if (dismissed || !onList) return null;

  function dismiss() {
    try {
      localStorage.setItem(CUTOVER_NOTICE_DISMISSED_KEY, 'true');
    } catch {
      // Storage refused (private mode): the notice closes for this visit only.
    }
    setDismissed(true);
  }

  return {
    id: 'cutover',
    label: t('cutoverNotice.label'),
    text: t('cutoverNotice.text'),
    icon: Info,
    details: t('cutoverNotice.text'),
    dismiss,
  };
}
